import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { documentService, productService } from '../../shared/utils/serviceProvider';
import stockManager from '../services/stockManager';
import { logAuditEvent, AuditAction, AuditLevel } from '../../shared/utils/auditLogger';
import { compareDocumentsNewestFirst } from '../../shared/utils/archiveDocumentOrdering';
import i18n from '../../i18n';

const dc = (key, opts) => i18n.t(`serviceMessages.documents.${key}`, opts);
const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

const STOCK_TARGET_STATUS_KEYS = {
  invoice: 'stock_target_status_invoice',
  purchase_order: 'stock_target_status_purchase_order',
  delivery_note: 'stock_target_status_delivery_note',
  reception: 'stock_target_status_reception',
  purchase: 'stock_target_status_purchase',
};

const getStockTargetStatusLabel = (docType) =>
  dc(STOCK_TARGET_STATUS_KEYS[docType] || 'stock_target_status_default');

/** Archives / création doc : statuts UI absents du CHECK SQLite → valeur persistée après normalisation archives */
const ARCHIVE_UI_STATUS_TO_DB_COERCE = {
  expired: 'overdue',
  negotiating: 'processing',
  accepted: 'approved',
  answered: 'completed',
  consolidated: 'completed',
  issued: 'sent',
  applied: 'completed',
  partially_paid: 'partial',
  in_transit: 'shipped',
  transferred: 'completed',
  verified: 'completed',
};

export const DocumentsContext = createContext();

// Custom hook for using the DocumentsContext
export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (context === undefined) {
    throw new Error(dc('hook_outside_provider'));
  }
  return context;
};

export const DocumentsProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

    // Load documents from database on mount
  useEffect(() => {
    // Create AbortController for this effect
    const abortController = new AbortController();
    
    const loadDocuments = async () => {
      try {
        const documentsData = await documentService.getDocuments({ signal: abortController.signal });
        if (documentsData && Array.isArray(documentsData)) {
          const sortedDocuments = documentsData.slice().sort(compareDocumentsNewestFirst);
          setDocuments(sortedDocuments);
          console.log(`Loaded ${sortedDocuments.length} documents from database (sorted newest first)`);
        } else {
          setDocuments([]);
          console.log('No documents found in database');
        }
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          console.log('Document loading was cancelled');
          return;
        }
        console.error('Error loading documents from database:', error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadDocuments();
    
    // Cleanup function to abort request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  // Refresh documents function (can be called externally)
  const refreshData = useCallback(async () => {
    // Create AbortController to handle request cancellation
    const abortController = new AbortController();
    
    try {
      setLoading(true);
      const documentsData = await documentService.getDocuments({ signal: abortController.signal });
      if (documentsData && Array.isArray(documentsData)) {
        const sortedDocuments = documentsData.slice().sort(compareDocumentsNewestFirst);
        setDocuments(sortedDocuments);
        console.log(`Refreshed ${sortedDocuments.length} documents from database (sorted newest first)`);
      } else {
        setDocuments([]);
        console.log('No documents found during refresh');
      }
    } catch (error) {
      // Ignore abort errors (they're expected when component unmounts)
      if (error.name === 'AbortError') {
        console.log('Document refresh was cancelled');
        return;
      }
      console.error('Error refreshing documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
    
    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, []);

  // Listen for refresh events from header
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Documents context received refresh event');
      refreshData();
    };

    // Listen for general refresh events
    window.addEventListener('refresh-documents', handleRefresh);
    window.addEventListener('refresh-all-data', handleRefresh);
    window.addEventListener('refresh-section', (e) => {
      if (e.detail?.section === 'archives') {
        handleRefresh();
      }
    });

    return () => {
      window.removeEventListener('refresh-documents', handleRefresh);
      window.removeEventListener('refresh-all-data', handleRefresh);
      window.removeEventListener('refresh-section', handleRefresh);
    };
  }, [refreshData]);

  // We don't need to save documents back to database on every change
  // as each operation will directly update the database

  // Add a new document
  const addDocument = useCallback(async (document) => {
    const newDocument = {
      ...document,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Add detailed logging
    console.log('Adding document with data:', {
      id: newDocument.id,
      type: newDocument.type,
      number: newDocument.number,
      title: newDocument.title || '',
      client: newDocument.client,
      date: newDocument.date,
      amount: newDocument.amount,
      status: newDocument.status,
      items: newDocument.items ? newDocument.items.length : 0
    });

    // Make sure we have a valid document type
    if (!newDocument.type || newDocument.type === 'Document') {
      // Map from document id format to standard type format
      // This fixes issues with hyphenated document types vs underscore format
      const typeMap = {
        'quote-request': 'quote_request',
        'purchase-order': 'purchase_order',
        'delivery-note': 'delivery',
        'shipping-slip': 'shipping',
        'material-exit': 'material_exit',
        'reception-note': 'reception_note',
        'return-slip': 'return_slip'
      };

      // Try to determine type from title
      if (newDocument.title && newDocument.title.toLowerCase().includes('facture')) {
        if (newDocument.title.toLowerCase().includes('proforma')) {
          newDocument.type = 'proforma';
        } else {
          newDocument.type = 'invoice';
        }
      } else if (newDocument.title && newDocument.title.toLowerCase().includes('devis')) {
        newDocument.type = 'quote';
      } else if (newDocument.title && newDocument.title.toLowerCase().includes('commande')) {
        newDocument.type = 'purchase_order';
      } else if (newDocument.title && newDocument.title.toLowerCase().includes('livraison')) {
        newDocument.type = 'delivery';
      } else if (newDocument.title && newDocument.title.toLowerCase().includes('sortie')) {
        newDocument.type = 'material_exit';
      } else {
        // Fix hyphenated document types
        if (newDocument.type && typeMap[newDocument.type]) {
          newDocument.type = typeMap[newDocument.type];
          console.log('Converted document type from hyphenated to underscore format:', newDocument.type);
        } else {
          console.warn('Document has no valid type, defaulting to "invoice"');
          newDocument.type = 'invoice';
        }
      }
    }

    if (!newDocument.title) {
      // Auto-generate a title based on the document type if missing
      if (newDocument.type === 'invoice') {
        newDocument.title = dc('title_create_invoice');
      } else if (newDocument.type === 'proforma') {
        newDocument.title = dc('title_proforma');
      } else if (newDocument.type === 'quote') {
        newDocument.title = dc('title_quote');
      }
      console.log('Auto-generated title:', newDocument.title);
    }

    if (!newDocument.items || newDocument.items.length === 0) {
      console.warn('Document has no items array or empty items!');
    }

    // Ensure we have a proper amount value
    if (newDocument.amount === undefined || newDocument.amount === null) {
      if (newDocument.items && newDocument.items.length > 0) {
        // Calculate amount from items
        newDocument.amount = newDocument.items.reduce((sum, item) => {
          const itemTotal = (Number(item.price) || 0) * (Number(item.quantity) || 0);
          return sum + itemTotal;
        }, 0);
        console.log('Calculated amount from items:', newDocument.amount);
      } else {
        newDocument.amount = 0;
        console.warn('No items to calculate amount, setting to 0');
      }
    }

    // Improve product information for better display in Archives view
    if (newDocument.items && Array.isArray(newDocument.items)) {
      newDocument.items = newDocument.items.map(item => {
        // If item is a simple ID string, try to convert it to a proper object
        if (typeof item === 'string') {
          return { product: item, name: dc('product_unnamed'), quantity: 1, price: 0 };
        }
        
        // If item is already an object
        if (typeof item === 'object' && item !== null) {
          // Ensure it has a name property
          if (!item.name && item.product) {
            // If product is a string, use it as name
            if (typeof item.product === 'string') {
              item.name = item.product;
            } 
            // If product is an object with name property
            else if (typeof item.product === 'object' && item.product && item.product.name) {
              item.name = item.product.name;
            }
          }
          
          // Additional fallbacks for name
          if (!item.name) {
            if (item.designation) item.name = item.designation;
            else if (item.description) item.name = item.description;
            else if (item.reference) item.name = item.reference;
            else item.name = dc('product_unnamed');
          }
          
          // Ensure it has quantity and price
          if (item.quantity === undefined) item.quantity = 1;
          if (item.price === undefined) item.price = 0;
        }
        
        return item;
      });
      
      console.log('Improved items data:', newDocument.items);
    }

    try {
      // Add to database using document service
      console.log('📄 Creating document:', newDocument);
      const createdDocument = await documentService.createDocument(newDocument);
      console.log('📄 API Response:', createdDocument);
      
      if (createdDocument) {
        // 📦 STOCK MANAGEMENT: Check if document is created with a status that requires immediate stock processing
        try {
          const stockOperation = stockManager.getStockOperation(createdDocument.type);
          if (stockOperation) {
            const currentStatus = createdDocument.status;
            
            // Check if document is created with a "completed" status that should immediately affect stock
            const shouldProcessStockImmediately = 
              (createdDocument.type === 'invoice' && (currentStatus === 'paid' || currentStatus === 'Payée')) ||
              (createdDocument.type === 'delivery_note' && (currentStatus === 'delivered' || currentStatus === 'Livré')) ||
              (createdDocument.type === 'purchase_order' && (currentStatus === 'received' || currentStatus === 'Reçu')) ||
              (createdDocument.type === 'reception' && (currentStatus === 'completed' || currentStatus === 'Terminé')) ||
              (createdDocument.type === 'purchase' && (currentStatus === 'completed' || currentStatus === 'Terminé'));
            
            if (shouldProcessStockImmediately) {
              console.log(`📦 Document created with ${currentStatus} status - processing stock immediately`);
              
              // Get current products for stock calculation
              const products = await productService.getProducts();
              
              // Update stock based on document items
              const stockMovements = await stockManager.updateStockFromDocument(createdDocument, products);
              
              if (stockMovements.length > 0) {
                console.log(`📦 Stock updated on creation: ${stockMovements.length} products affected`);
                
                // Emit stock update event for UI refresh
                eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
                
                // Store stock movements in document metadata
                if (createdDocument.id) {
                  await documentService.updateDocument(createdDocument.id, {
                    metadata: JSON.stringify({ 
                      stockMovements, 
                      stockProcessed: true,
                      stockProcessedOn: currentStatus,
                      stockProcessedDate: new Date().toISOString()
                    })
                  }).catch(err => console.warn('Could not save stock metadata:', err));
                }
              }
            } else {
              console.log(`📦 Document ${createdDocument.type} created with ${currentStatus || 'pending'} status - stock will be updated when status changes`);
              console.log(`📦 For ${createdDocument.type}: stock updates when status becomes:`, 
                getStockTargetStatusLabel(createdDocument.type)
              );
            }
          } else {
            console.log('📦 No stock impact for document type:', createdDocument.type);
          }
        } catch (stockError) {
          console.error('📦 Stock management error during document creation:', stockError);
          // Don't fail document creation if stock management fails
        }
        
        // Update local state after database operation
        setDocuments((prev) => {
          const id = createdDocument.id;
          const withoutDup = prev.filter((d) => d.id !== id);
          const updated = [...withoutDup, createdDocument].sort(compareDocumentsNewestFirst);
          console.log(
            `Document merged after create (sorted newest first). Total documents: ${updated.length}`,
          );
          
          // Emit events for real-time updates
          eventEmitter.emit(EVENT_TYPES.DOCUMENT_CREATED, createdDocument);
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          AuditAction.DOCUMENT_CREATE,
          dc('audit_document_label', {
            type: createdDocument.type || dc('default_type'),
            identifier: createdDocument.number || createdDocument.title || '',
          }),
          dc('audit_create_detail', {
            client: createdDocument.client?.name || createdDocument.client || cm('unknown'),
          }),
          AuditLevel.SUCCESS
        );
        
        return createdDocument;
      } else {
        console.error('❌ createdDocument is falsy:', createdDocument);
        throw new Error(dc('create_failed'));
      }
    } catch (error) {
      console.error('❌ Error creating document:', error);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  }, []);

  // Update an existing document
  const updateDocument = useCallback(async (id, updatedDocument) => {
    try {
      // Validation basique des paramètres
      if (!id || id === 'undefined' || !updatedDocument) {
        console.warn('⚠️ updateDocument: missing ID or data:', { id, updatedDocument });
        return null;
      }
      
      const oldDoc = documents.find(doc => doc.id === id);
      if (!oldDoc) {
        console.warn(
          `⚠️ Document ${id} absent du cache local — mise à jour envoyée à l’API sans état précédent (Archives peuvent avoir besoin d’un refresh).`
        );
      }
      
      console.log('📄 DocumentsContext.updateDocument - Input:', {
        id,
        updatedDocument,
        oldDocFields: oldDoc ? Object.keys(oldDoc) : [],
        oldDocSample: oldDoc
          ? {
              type: oldDoc.type,
              status: oldDoc.status,
              number: oldDoc.number
            }
          : null
      });
      
      // Full archive payload — backend merges then writes all columns (same as POST create).
      const safeFieldsToUpdate = { ...updatedDocument };
      delete safeFieldsToUpdate.id;
      delete safeFieldsToUpdate.createdAt;
      delete safeFieldsToUpdate.created_at;
      safeFieldsToUpdate.updated_at = new Date().toISOString();

      // Update document using document service
      const resultRaw = await documentService.updateDocument(id, safeFieldsToUpdate);

      /*
       * Si SQLite renvoie encore `draft` alors qu’une clé UI était demandée (ex. expired→overdue),
       * éviter badge « Brouillon » alors que le toast affiche déjà « Expiré » etc.
       */
      let result = resultRaw;
      if (resultRaw) {
        const uiKey =
          typeof updatedDocument.status === 'string' ? updatedDocument.status.trim() : '';
        const coerceDb = uiKey ? ARCHIVE_UI_STATUS_TO_DB_COERCE[uiKey] : '';
        const apiStatusNorm =
          resultRaw.status != null && String(resultRaw.status).trim() !== ''
            ? String(resultRaw.status).trim().toLowerCase()
            : '';
        const apiLooksWrong = !apiStatusNorm || apiStatusNorm === 'draft';
        if (coerceDb && apiLooksWrong) {
          result = { ...resultRaw, status: coerceDb };
        }
      }
      
      if (result) {
        // 📦 STOCK MANAGEMENT: Handle status changes that affect stock
        try {
          if (!oldDoc) {
            console.log(`🔍 STOCK DEBUG: skipping stock reconcile (document was not in local cache)`);
          } else {
          const oldStatus = oldDoc.status;
          const newStatus = updatedDocument.status;
          
          console.log(`🔍 STOCK DEBUG: Checking status change - oldStatus: ${oldStatus}, newStatus: ${newStatus}, docType: ${oldDoc.type}`);
          
          if (oldStatus !== newStatus && newStatus) {
            console.log(`📦 Status change detected: ${oldStatus} → ${newStatus} for ${oldDoc.type}`);
            
            const stockOperation = stockManager.getStockOperation(oldDoc.type);
            console.log(`🔍 STOCK DEBUG: stockOperation for ${oldDoc.type}:`, stockOperation);
            if (stockOperation) {
              // Define which status changes trigger stock updates (French status names)
              const shouldUpdateStock = 
                (oldDoc.type === 'invoice' && (newStatus === 'paid' || newStatus === 'Payée')) ||                    // Invoice paid = reduce stock
                (oldDoc.type === 'delivery_note' && (newStatus === 'delivered' || newStatus === 'Livré')) ||         // Delivery = reduce stock
                (oldDoc.type === 'purchase_order' && (newStatus === 'received' || newStatus === 'Reçu' || newStatus === 'paid' || newStatus === 'Payée')) || // Purchase received/paid = increase stock
                (oldDoc.type === 'reception' && (newStatus === 'completed' || newStatus === 'Terminé')) ||           // Reception completed = increase stock
                (oldDoc.type === 'purchase' && (newStatus === 'completed' || newStatus === 'Terminé'));              // Purchase completed = increase stock
              
              console.log(`🔍 STOCK DEBUG: shouldUpdateStock = ${shouldUpdateStock}`);
              
              if (shouldUpdateStock) {
                console.log(`📦 PROCESSING STOCK: ${oldDoc.type} status changed to ${newStatus}`);
                console.log(`📦 STOCK OPERATION: ${stockOperation.operation} (${stockOperation.operation === 'INCREASE' ? '+' : '-'})`);
                
                // Get current products for stock calculation
                const products = await productService.getProducts();
                console.log(`📦 Loaded ${products.length} products for stock calculation`);
                
                // Update stock based on document items
                const stockMovements = await stockManager.updateStockFromDocument(oldDoc, products);
                
                if (stockMovements.length > 0) {
                  console.log(`📦 Stock updated on status change: ${stockMovements.length} products affected`);
                  
                  // Emit stock update event for UI refresh
                  eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
                  
                  // Store stock movements in document metadata
                  if (result.id) {
                    await documentService.updateDocument(result.id, {
                      metadata: JSON.stringify({ 
                        stockMovements, 
                        stockProcessed: true,
                        stockProcessedOn: newStatus,
                        stockProcessedDate: new Date().toISOString()
                      })
                    }).catch(err => console.warn('Could not save stock metadata:', err));
                  }
                }
              } else {
                console.log(`📦 Status change to "${newStatus}" does not trigger stock update for ${oldDoc.type}`);
              }
            }
          }
          }
        } catch (stockError) {
          console.error('📦 Stock management error during status change (document still updated):', stockError);
          // Don't fail document update if stock management fails
        }
        
        // Update local state after database operation
        setDocuments((prev) => {
          const ix = prev.findIndex((doc) => doc.id === id);
          let next =
            ix >= 0 ? prev.map((doc, i) => (i === ix ? result : doc)) : [result, ...prev];
          next = next.slice().sort(compareDocumentsNewestFirst);

          console.log('Document updated:', id, updatedDocument);

          if (
            oldDoc &&
            updatedDocument.amount !== undefined &&
            oldDoc.amount !== updatedDocument.amount
          ) {
            eventEmitter.emit(EVENT_TYPES.DOCUMENT_AMOUNT_CHANGED, {
              id,
              oldAmount: oldDoc.amount,
              newAmount: updatedDocument.amount
            });
          }

          eventEmitter.emit(EVENT_TYPES.DOCUMENT_UPDATED, {
            id,
            oldDocument: oldDoc || null,
            newDocument: result
          });

          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);

          return next;
        });

        // Log audit event
        logAuditEvent(
          AuditAction.DOCUMENT_UPDATE,
          dc('audit_document_label', {
            type: result.type || dc('default_type'),
            identifier: result.number || result.title || '',
          }),
          dc('audit_update_detail', { id }),
          AuditLevel.INFO
        );

        return result;
      } else {
        throw new Error(dc('update_failed'));
      }
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }, [documents]);

  // Delete a document
  const deleteDocument = useCallback(async (id) => {
    try {
      const documentToDelete = documents.find(doc => doc.id === id);
      if (!documentToDelete) {
        throw new Error(dc('not_found', { id }));
      }
      
      let apiSucceeded = false;
      try {
        // Delete from database using document service
        await documentService.deleteDocument(id);
        apiSucceeded = true;
      } catch(apiError) {
        console.warn('API delete failed, falling back to local delete:', apiError.message || apiError);
        // If the failure is a network error (Failed to fetch) we still proceed locally
      }
      
      // Update local state after database operation or offline fallback
      setDocuments(prev => {
        const updated = prev.filter(doc => doc.id !== id);
        console.log('Document deleted locally:', id, apiSucceeded ? '' : '(offline mode)');
        
        // Emit delete event
        eventEmitter.emit(EVENT_TYPES.DOCUMENT_DELETED, documentToDelete);
        eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
        
        return updated;
      });
      
      // Log audit event
      logAuditEvent(
        AuditAction.DOCUMENT_DELETE,
        dc('audit_document_label', {
          type: documentToDelete.type || dc('default_type'),
          identifier: documentToDelete.number || documentToDelete.title || '',
        }),
        dc('audit_delete_detail', { id }),
        AuditLevel.WARNING
      );
      
      return { success: true, id, offline: !apiSucceeded };
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }, [documents]);

  // Get a document by ID
  const getDocumentById = useCallback((id) => {
    return documents.find(doc => doc.id === id);
  }, [documents]);

  // Filter documents by various criteria
  const filterDocuments = useCallback((filters) => {
    return documents.filter(doc => {
      let match = true;

      if (filters.client && doc.client !== filters.client) {
        match = false;
      }

      if (filters.status && doc.status !== filters.status) {
        match = false;
      }

      if (filters.type && doc.type !== filters.type) {
        match = false;
      }

      if (filters.dateFrom) {
      const docDate = new Date(doc.date);
        const fromDate = new Date(filters.dateFrom);
        if (docDate < fromDate) {
          match = false;
        }
      }

      if (filters.dateTo) {
      const docDate = new Date(doc.date);
        const toDate = new Date(filters.dateTo);
        if (docDate > toDate) {
          match = false;
        }
      }

      return match;
    });
  }, [documents]);

  // Value object to be provided to consumers
  const value = {
        documents,
    loading,
        addDocument,
        updateDocument,
        deleteDocument,
        getDocumentById,
    filterDocuments,
    refreshData
  };

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

